// **The shared prompt-bar shell** (`demo-presentation-shell` design **DD6**). Reacts to
// `prototype/sketches/demo-presentation-shell/stage-hud-states.html` panels C ("Bees" scope,
// busy), D ("Beekeeper" scope, idle) and E (the failure state).
//
// One control, not two surfaces: `InstructPromptBar.tsx` (chapter 1b) and `BeekeeperPromptBar.tsx`
// (chapter 3) each own their own submission logic and render through this same shell, changing
// only the `scopeLabel` prop and what `onSubmit` does — the script's whole chapter-3 pivot ("if I
// type into here now, I'm no longer talking to the bees") made visible as one word changing on a
// control the audience has already seen, never a second control appearing.
//
// **Reply bubbles are display state, owned here** (`demo-presentation-shell` Track D, user
// stories 15/16). A caller only ever hands this shell the *latest* reply as a plain string prop
// (`InstructPromptBar` keeps computing it exactly as before); every distinct value that arrives is
// this component's cue to append one bubble to a small on-screen queue, newest nearest the input,
// capped at `REPLY_BUBBLE_CAP` and each fading off on its own `REPLY_BUBBLE_LIFETIME_MS` clock —
// none of which the caller needs to know about. That queue and its timers live entirely in this
// component's state, never lifted into a controller: `PromptBar` still never decides *what*
// Enter/Send does, but it does now decide how long an answer stays visible once given.
//
// **Decision: a beat change (`resetKey`) clears the queue; a draft edit does not.**
// `InstructPromptBar` already clears its own copy of `replyText` to `undefined` on every
// keystroke (guarding against re-showing a stale reply once the presenter starts the next
// sentence) and again on a cursor change. Neither transition reaches this component as a request
// to clear anything: a prop going back to `undefined`/empty is read as "nothing new to add", not
// "take away what's already showing" (see the `replyText` effect below) — so a bubble already on
// screen keeps fading on its own schedule through a keystroke, which is what lets the presenter
// see the last reply while composing the next line, exactly the audience-facing point of a queue
// instead of a single slot. A beat move is different in kind, not just in degree: the show has
// moved on, so a reply — or a **pinned failure** — left over from the beat before it is stale, not
// merely superseded, and instant clearing is the wanted behaviour. `resetKey` (wired from
// `InstructPromptBar`'s existing `cursor` prop) is a distinct signal for exactly that: any change
// empties the queue and cancels its timers immediately, whatever text ever populated it. The
// failure state itself is untouched by any of this — it is a single pinned slot gated by `status`,
// never a queued bubble, so it already never fades (user story 16); the caller clearing it
// (`InstructPromptBar`'s cursor effect resets `status`/`failureMessage`) is what removes it.
//
// A bubble is markup only, never an interactive target, and its stack sits `pointer-events: none`
// so it can never eat a click meant for the world behind the stage — chapter 1 is
// point-and-click.

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { submitOnEnter } from '../../ui/keyboard-policy';
import { Z } from './z-layers';

export type PromptBarStatus = 'idle' | 'busy' | 'failed';

interface ReplyBubble {
  id: number;
  text: string;
}

/** How long a reply bubble stays on screen before it fades (user story 15). */
export const REPLY_BUBBLE_LIFETIME_MS = 5000;

/** The stack never holds more than this many replies at once; the oldest is evicted first. */
export const REPLY_BUBBLE_CAP = 3;

export interface PromptBarProps {
  scopeLabel: string;
  draft: string;
  status: PromptBarStatus;
  /** Shown only while `status === 'failed'` — short and audience-safe (design DD7's own words:
   * never a raw body, never a stack trace). The bar keeps its **input** alongside it: the failure
   * used to *replace* the textarea, so a chapter that failed once could never be typed into again
   * (E2E finding N1 — the presenter's own documented recovery, "just type it again", was the one
   * thing the surface made impossible). */
  failureMessage?: string;
  /**
   * The latest completed reply. Every distinct value appends one bubble to this shell's own
   * queue (see the module header) — the caller does not manage the queue, only the latest value.
   * A change back to `undefined`/empty adds nothing and clears nothing; already-queued bubbles
   * keep fading on their own clock.
   */
  replyText?: string;
  /**
   * Clears the whole reply-bubble queue immediately on any change — a beat move, not a draft
   * edit (module header). Wired from `InstructPromptBar`'s sequencer `cursor`; omitted, nothing
   * clears via this path (mirrors `cursor`'s own "nothing to offer" default).
   */
  resetKey?: number;
  onChangeDraft(text: string): void;
  onSubmit(): void;
  /** Present only when `status === 'failed'` — "Try again" re-sends the same text. */
  onRetry(): void;
}

// Restyled to mirror the normal gameplay prompt bar (`SandboxScreen.tsx`'s pinned Beekeeper
// input): a `--surface-card` field with the scope as a leading pill inside it, and an
// accent-fill Send button beside it — so the demo's chat surface reads as the same control the
// game itself shows, not a demo-only variant.
const BAR_STYLE = {
  position: 'absolute' as const,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: Z.hudBar,
  padding: '8px 12px 14px',
};

function rowStyle(): CSSProperties {
  return { display: 'flex', alignItems: 'center', gap: 8 };
}

function fieldStyle(failed: boolean): CSSProperties {
  return {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 44,
    boxSizing: 'border-box',
    padding: '0 6px',
    borderRadius: 'var(--r-md)',
    border: `1.5px solid ${failed ? 'var(--danger, #C1401F)' : 'var(--line-strong)'}`,
    background: failed ? 'var(--danger-soft, #FBE4DC)' : 'var(--surface-card)',
  };
}

const CHIP_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  flex: 'none',
  height: 30,
  padding: '0 8px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--accent-soft)',
  border: '1px solid var(--honey-300)',
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  color: 'var(--accent-on-soft)',
};

const INPUT_STYLE = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: 'var(--ink-1)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  lineHeight: 1.45,
  resize: 'none' as const,
};

const SEND_STYLE = (enabled: boolean): CSSProperties => ({
  flex: 'none',
  height: 'var(--hit-target)',
  minWidth: 'var(--hit-target)',
  padding: '0 18px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--accent-fill)',
  border: '1px solid var(--accent-strong)',
  color: '#FFFDF6',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 14,
  opacity: enabled ? 1 : 0.38,
  cursor: enabled ? 'pointer' : 'default',
});

// The queue container sits ahead of the input row in document order — "above" it, given the bar's
// own bottom-pinned, top-to-bottom stack (BAR_STYLE) — and never captures a pointer event: a
// bubble is markup only, and chapter 1's point-and-click gestures must reach the world behind it.
const REPLY_STACK_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 6,
  pointerEvents: 'none',
};

const REPLY_BUBBLE_STYLE: CSSProperties = {
  alignSelf: 'flex-end',
  maxWidth: '85%',
  padding: '6px 10px',
  borderRadius: 'var(--r-md)',
  border: '1px solid var(--line-strong)',
  background: 'var(--surface-card)',
  color: 'var(--ink-2)',
  fontSize: 13,
  lineHeight: 1.4,
};

const RETRY_STYLE: CSSProperties = {
  flex: 'none',
  height: 'var(--hit-target)',
  minWidth: 'var(--hit-target)',
  padding: '0 14px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--surface-card)',
  border: '1.5px solid var(--line-strong)',
  color: 'var(--ink-1)',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 13,
  cursor: 'pointer',
};

/** `PromptBar` itself never decides *what* Enter/Send does — every state transition lives in the
 * caller's controller (`InstructPromptBar`/`BeekeeperPromptBar`), so this stays presentational.
 * The reply-bubble queue is the one exception, and it is display state, not a decision about
 * Enter/Send — see the module header. */
export function PromptBar({
  scopeLabel,
  draft,
  status,
  failureMessage,
  replyText,
  resetKey,
  onChangeDraft,
  onSubmit,
  onRetry,
}: PromptBarProps): ReactElement {
  const busy = status === 'busy';
  const failed = status === 'failed';

  const [bubbles, setBubbles] = useState<ReplyBubble[]>([]);
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const nextBubbleIdRef = useRef(0);
  // Guards on an actual *change*, mirroring `InstructPromptBar`'s own `cursor` guard — the initial
  // value IS this render's value, so mounting with a reply/resetKey already set clears or adds
  // nothing that isn't genuinely new.
  const lastReplyTextRef = useRef(replyText);
  const lastResetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (replyText === lastReplyTextRef.current) {
      return;
    }
    lastReplyTextRef.current = replyText;
    if (replyText === undefined || replyText.length === 0) {
      // Nothing to add — and deliberately nothing to clear either (module header).
      return;
    }
    const id = nextBubbleIdRef.current++;
    setBubbles((current) => {
      const next = [...current, { id, text: replyText }];
      const evicted = next.splice(0, next.length - REPLY_BUBBLE_CAP);
      for (const bubble of evicted) {
        const timer = timersRef.current.get(bubble.id);
        if (timer !== undefined) {
          clearTimeout(timer);
          timersRef.current.delete(bubble.id);
        }
      }
      return next;
    });
    timersRef.current.set(
      id,
      setTimeout(() => {
        timersRef.current.delete(id);
        setBubbles((current) => current.filter((bubble) => bubble.id !== id));
      }, REPLY_BUBBLE_LIFETIME_MS),
    );
  }, [replyText]);

  useEffect(() => {
    if (resetKey === lastResetKeyRef.current) {
      return;
    }
    lastResetKeyRef.current = resetKey;
    for (const timer of timersRef.current.values()) {
      clearTimeout(timer);
    }
    timersRef.current.clear();
    setBubbles([]);
  }, [resetKey]);

  // A chapter boundary unmounts this bar (`DemoApp.tsx` keys it by chapter) — cancel any timers
  // still pending rather than let one fire a `setState` after the component is gone.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, []);

  const submit = (): boolean => {
    if (busy || draft.trim().length === 0) {
      return false;
    }
    onSubmit();
    return true;
  };

  return (
    <div data-testid="hud-promptBar" style={BAR_STYLE}>
      {bubbles.length > 0 && (
        <div data-testid="prompt-bar-replies" style={REPLY_STACK_STYLE}>
          {bubbles.map((bubble) => (
            <div key={bubble.id} data-testid="prompt-bar-reply" style={REPLY_BUBBLE_STYLE}>
              {bubble.text}
            </div>
          ))}
        </div>
      )}
      <div style={rowStyle()}>
        <div style={fieldStyle(failed)}>
          <span
            style={
              failed
                ? { ...CHIP_STYLE, background: 'var(--danger, #C1401F)', border: 'none', color: '#FFF6F3' }
                : CHIP_STYLE
            }
          >
            {scopeLabel}
          </span>
          <textarea
            data-testid="prompt-bar-input"
            aria-label={`Tell the ${scopeLabel.toLowerCase()} what to do`}
            // Borderless child of the field frame above, which owns its padding and height. The
            // demo keeps its own frame rather than the shell's composite one — it carries a danger
            // border this surface needs — so it opts out of the standalone-control metrics here.
            data-ui-field-control=""
            rows={1}
            value={draft}
            disabled={busy}
            placeholder={`Tell the ${scopeLabel.toLowerCase()} what to do`}
            style={INPUT_STYLE}
            onChange={(event) => onChangeDraft(event.target.value)}
            onKeyDown={submitOnEnter(submit)}
          />
          {busy && <span data-testid="prompt-bar-busy" style={{ flex: 'none', fontSize: 13, color: 'var(--ink-2)', paddingRight: 8 }}>Working…</span>}
        </div>
        {!busy && failed && (
          <button type="button" data-testid="prompt-bar-retry" style={RETRY_STYLE} onClick={onRetry}>
            Try again
          </button>
        )}
        {!busy && (
          <button
            type="button"
            data-testid="prompt-bar-send"
            style={SEND_STYLE(draft.trim().length > 0)}
            // No `onMouseDown` focus guard here, deliberately, though SandboxScreen's Send has
            // one: this bar's geometry does not change on blur, and the click MUST move focus out
            // of the textarea — the demo's cue keys are inert while a text surface is focused
            // (DD11), so a Send that kept focus would swallow the presenter's next advance.
            onClick={submit}
          >
            Send
          </button>
        )}
      </div>
      {failed && (
        <div
          data-testid="prompt-bar-failure"
          style={{ marginTop: 6, fontSize: 13, color: 'var(--danger-ink, #7E2C1B)' }}
        >
          {failureMessage}
        </div>
      )}
    </div>
  );
}
